import { useState } from 'react';
import { oversightHeld } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { Act, useApi } from './common';
import { Summary } from './Summary';
import type { LedgerIntent, OversightTab } from './OversightPage';

/**
 * Oversight > Summary on the V9 design (monPlainShow, T15640): one plain line on what is
 * waiting for a person, Review when anything is, "Show proof details" for the live board, and
 * the "What Oversight is for" box (the V9 empty state) with its first two steps. The held
 * count is the server's (GET /oversight/held); nothing is shown until it has answered.
 */
export function SummaryHome({ onGo }: { onGo: (t: OversightTab, i?: LedgerIntent) => void }) {
  const [proof, setProof] = useState(false);
  const held = useApi((c, signal) => oversightHeld(c, { query: { limit: 100 }, signal }), [], { pollMs: 15000 });
  const n = held.data ? held.data.items.length : null;
  const head = n === null ? '' : n === 0 ? 'Nothing is waiting for a person' : 'Review ' + n + ' waiting';
  const sub =
    n === null
      ? ''
      : n === 0
        ? 'Atlas is running inside its rules. Every decision is recorded.'
        : n + (n === 1 ? ' decision was' : ' decisions were') + ' held for a person to check before it goes out.';
  const BTN = 'display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
  return (
    <>
      <div data-testid="ov-plain" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;padding:14px 16px;margin-bottom:10px;border-radius:12px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
        <div style={sx('flex:1 1 320px;min-width:0;display:flex;flex-direction:column;gap:3px;')}>
          {head && <span data-testid="ov-plain-head" style={sx('font-size:19px;font-weight:800;color:#22272E;')}>{head}</span>}
          {sub && <span style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>{sub}</span>}
          {held.error && <span style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>{held.error.message}</span>}
        </div>
        {!!n && (
          <Act onClick={() => onGo('live')} css="display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;">
            Review
          </Act>
        )}
        <Act onClick={() => setProof((p) => !p)} pressed={proof} css="display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.35);">
          {proof ? 'Hide proof details' : 'Show proof details'}
        </Act>
        <div data-testid="ov-empty" style={sx('flex:1 1 100%;box-sizing:border-box;display:flex;flex-direction:column;gap:6px;margin-top:10px;padding:14px 16px;border-radius:10px;border:1px dashed rgba(34,39,46,.25);background:#FCFAF4;')}>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>What Oversight is for</span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.5;max-width:90ch;text-wrap:pretty;')}>
            Every decision Atlas makes passes a check before it reaches a resident, a family or a chart. Live Decisions shows each one as it happens; the Truth Ledger keeps the sealed record a surveyor or lawyer can verify. Start with Live Decisions to see what Atlas did today.
          </span>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:2px;')}>
            <Act onClick={() => onGo('live')} css={BTN + 'background:#2F5C97;color:#fff;'}>
              Open Live Decisions
            </Act>
            <Act onClick={() => onGo('ledger')} css={BTN + 'border:1px solid rgba(34,39,46,.22);color:#22272E;'}>
              Open Truth Ledger
            </Act>
          </div>
        </div>
      </div>
      {proof && <Summary onGo={onGo} />}
    </>
  );
}
